import * as React from 'react';
import {
  type ListNotificationsResponse,
  type NotificationDto,
  isNotificationStaleRunning,
} from '@protoss/shared';
import { apiFetch } from '../lib/apiClient';

// Poll faster while a job is RUNNING (so the toaster chip appears + its timer/resolution feel live);
// fall back to a relaxed cadence when idle.
const RUNNING_POLL_MS = 8_000;
const IDLE_POLL_MS = 45_000;

export interface UseNotificationsResult {
  notifications: NotificationDto[];
  unreadCount: number;
  loading: boolean;
  refresh: () => void;
  markRead: (uuid: string) => void;
  markAllRead: () => void;
}

/**
 * Polls the header bell's notifications. REST poll (every 45s + on mount + on window focus) keeps the
 * badge fresh without a dedicated socket; a background job that finishes appears within a poll cycle.
 * Read-state mutations are optimistic + reconciled by the next poll. Disabled until a workspace is
 * active (the server scopes by the user's membership). `workspaceKey` re-polls on workspace switch.
 */
export function useNotifications(enabled: boolean, workspaceKey?: string | null): UseNotificationsResult {
  const [notifications, setNotifications] = React.useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  // True while any notification is 'running' - drives the faster poll cadence.
  const hasRunningRef = React.useRef(false);

  const load = React.useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await apiFetch<ListNotificationsResponse>('/api/workspace/notifications');
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
      // Only LIVE running jobs drive the fast cadence - a stale 'running' (its job died) must not pin
      // the poll at 8s forever, so it's excluded and the poll relaxes back to idle.
      hasRunningRef.current = res.notifications.some(
        (n) => n.status === 'running' && !isNotificationStaleRunning(n)
      );
    } catch {
      // best-effort: a transient failure just leaves the prior state; the next poll retries.
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Adaptive poll (recursive timeout): fast while a job runs, relaxed when idle; + on window focus.
  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: number | undefined;
    const tick = async (): Promise<void> => {
      await load();
      if (cancelled) return;
      timer = window.setTimeout(() => void tick(), hasRunningRef.current ? RUNNING_POLL_MS : IDLE_POLL_MS);
    };
    void tick();
    const onFocus = (): void => void load();
    // Immediate refresh when something signals a likely change (e.g. a chat run just ended and may
    // have kicked off a background job) - so the toaster/bell appear now, not on the next poll.
    const onRefresh = (): void => void load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('protoss:notifications-refresh', onRefresh);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('protoss:notifications-refresh', onRefresh);
    };
  }, [enabled, workspaceKey, load]);

  const markRead = React.useCallback((uuid: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.uuid === uuid && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    apiFetch(`/api/workspace/notifications/${uuid}/read`, { method: 'POST' }).catch(() => void load());
  }, [load]);

  const markAllRead = React.useCallback(() => {
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })));
    setUnreadCount(0);
    apiFetch('/api/workspace/notifications/read-all', { method: 'POST' }).catch(() => void load());
  }, [load]);

  return { notifications, unreadCount, loading, refresh: () => void load(), markRead, markAllRead };
}
